/**
 * DR Active Ingredients - Cover Flow Stack
 */
(function () {
  'use strict';

  function updateDotsLine(dots, container) {
    if (!dots.length || !container) return;

    if (dots.length <= 1) {
      container.style.setProperty('--dots-line-width', '0px');
      container.style.setProperty('--dots-line-left', '50%');
    } else {
      var first = dots[0];
      var last = dots[dots.length - 1];

      var firstRect = first.getBoundingClientRect();
      var lastRect = last.getBoundingClientRect();
      var containerRect = container.getBoundingClientRect();

      var firstCenter = firstRect.left + firstRect.width / 2;
      var lastCenter = lastRect.left + lastRect.width / 2;

      var leftCenter = Math.min(firstCenter, lastCenter);
      var rightCenter = Math.max(firstCenter, lastCenter);

      var width = Math.max(0, rightCenter - leftCenter);
      var left = leftCenter - containerRect.left;

      container.style.setProperty('--dots-line-width', width + 'px');
      container.style.setProperty('--dots-line-left', left + 'px');
    }
  }

  function initStack(root) {
    if (!root || root.dataset.initialized === '1') return;
    root.dataset.initialized = '1';

    var stackEl = root.querySelector('.dr-ingredients-stack');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-ingredient-card]'));
    var dotsContainer = root.querySelector('[data-upsell-dots]');
    var dots = dotsContainer ? Array.prototype.slice.call(dotsContainer.querySelectorAll('[data-upsell-dot]')) : [];

    if (!cards.length) return;

    var activeIndex = 0;
    var totalCards = cards.length;

    // Helper: positive modulo
    function mod(n, m) {
      return ((n % m) + m) % m;
    }

    function renderState() {
      // Clear existing classes
      cards.forEach(function (card) {
        card.className = 'dr-ingredients-card';
      });

      // Assign new classes based on activeIndex
      cards[activeIndex].classList.add('is-center');

      if (totalCards > 1) {
        // Next 1
        var next1 = mod(activeIndex + 1, totalCards);
        cards[next1].classList.add('is-left-1'); // In RTL, "next" is on physical left

        // Prev 1
        var prev1 = mod(activeIndex - 1, totalCards);
        cards[prev1].classList.add('is-right-1'); // In RTL, "prev" is on physical right

        if (totalCards > 3) {
          // Next 2
          var next2 = mod(activeIndex + 2, totalCards);
          cards[next2].classList.add('is-left-2');

          if (totalCards > 4) {
            // Prev 2
            var prev2 = mod(activeIndex - 2, totalCards);
            cards[prev2].classList.add('is-right-2');
          } else {
             // If exactly 4 cards, the 4th is currently is-left-2. We won't have a prev-2.
          }
        }
      }

      // Update dots
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === activeIndex);
        dot.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
      });
      
      updateDotsLine(dots, dotsContainer);
    }

    var isAnimating = false;
    var shiftTimeout;
    var enterTimeout;

    function setActiveIndex(index) {
      if (isAnimating) return;
      
      var targetIndex = mod(index, totalCards);
      if (targetIndex === activeIndex) return;

      isAnimating = true;

      var forwardDistance = mod(targetIndex - activeIndex, totalCards);
      var backwardDistance = mod(activeIndex - targetIndex, totalCards);
      
      // Determine the shortest path
      var isNext = forwardDistance <= backwardDistance;
      var distance = isNext ? forwardDistance : backwardDistance;
      
      // If the target is more than 1 step away, scrub through the intermediate step first
      if (distance > 1) {
        var intermediateIndex = isNext ? mod(activeIndex + 1, totalCards) : mod(activeIndex - 1, totalCards);
        
        // Temporarily adjust animation timing for a fast scrub
        cards.forEach(function(c) {
          c.style.transitionDuration = '0.2s';
        });

        // First step of the scrub
        performShift(intermediateIndex, isNext, 200, 250, function() {
          // Immediately after the first step finishes, fire the next step towards the final target
          // Keep it fast until we are doing the final step
          var remainingDistance = distance - 1;
          if (remainingDistance > 1) {
            // Recursive scrub for very large jumps
            isAnimating = false; // Briefly release lock for recursive call
            setActiveIndex(targetIndex); 
          } else {
            // Final step, restore normal timing
            cards.forEach(function(c) {
              c.style.transitionDuration = '';
            });
            isAnimating = false;
            setActiveIndex(targetIndex);
          }
        });
        return;
      }

      // Single step navigation (normal)
      performShift(targetIndex, isNext, 400, 600, function() {
        isAnimating = false;
      });
    }

    function performShift(targetIndex, isNext, shiftTime, totalTime, onComplete) {
      var currentCard = cards[activeIndex];
      var targetCard = cards[targetIndex];
      
      if (isNext) {
        currentCard.classList.add('is-shifting-right');
      } else {
        currentCard.classList.add('is-shifting-left');
      }

      // Temporarily elevate the incoming card so it doesn't slide under other background cards
      targetCard.classList.add('is-shifting-target');

      clearTimeout(shiftTimeout);
      clearTimeout(enterTimeout);
      
      shiftTimeout = setTimeout(function() {
        currentCard.classList.remove('is-shifting-left', 'is-shifting-right');
        targetCard.classList.remove('is-shifting-target');
        activeIndex = targetIndex;
        renderState();
        
        enterTimeout = setTimeout(function() {
          if (onComplete) onComplete();
        }, totalTime - shiftTime); // Wait for the state transition to finish
      }, shiftTime); // Phase 1: move aside duration
    }

    // Drag / Swipe logic
    var dragStartX = 0;
    var dragStartY = 0;
    var isDragging = false;
    var dragMoved = false; // Prevents click event if a drag occurred
    var SWIPE_THRESHOLD = 30;

    function handleDragStart(clientX, clientY) {
      isDragging = true;
      dragStartX = clientX;
      dragStartY = clientY;
      stackEl.classList.add('is-swiping');
    }

    function handleDragEnd(clientX, clientY) {
      if (!isDragging) return;
      isDragging = false;
      stackEl.classList.remove('is-swiping');
      
      var deltaX = clientX - dragStartX;
      var deltaY = clientY - dragStartY;

      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaY) > Math.abs(deltaX)) {
        return;
      }

      dragMoved = true;
      setTimeout(function() { dragMoved = false; }, 50);

      // Reversed swipe direction for RTL
      if (deltaX < 0) {
        setActiveIndex(activeIndex - 1);
      } else {
        setActiveIndex(activeIndex + 1);
      }
    }

    if (stackEl) {
      // Touch events
      stackEl.addEventListener('touchstart', function (e) {
        handleDragStart(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
      }, { passive: true });

      stackEl.addEventListener('touchend', function (e) {
        handleDragEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
      }, { passive: true });

      // Mouse events
      stackEl.addEventListener('mousedown', function (e) {
        handleDragStart(e.clientX, e.clientY);
      });

      window.addEventListener('mouseup', function (e) {
        handleDragEnd(e.clientX, e.clientY);
      });
    }

    // Card tap logic
    cards.forEach(function (card, index) {
      card.addEventListener('click', function (e) {
        if (dragMoved) return; // Prevent click if user was dragging

        if (index !== activeIndex) {
          setActiveIndex(index);
        } else {
          // If clicking the center card, navigate based on which side was clicked
          var rect = card.getBoundingClientRect();
          var clickX = e.clientX - rect.left;
          
          if (clickX < rect.width / 2) {
            // Clicked left half -> go to physical left (RTL: next)
            setActiveIndex(activeIndex + 1);
          } else {
            // Clicked right half -> go to physical right (RTL: prev)
            setActiveIndex(activeIndex - 1);
          }
        }
      });
    });

    // Dots logic
    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        setActiveIndex(index);
      });
    });

    // Initial render via IntersectionObserver to trigger animation on scroll reveal
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting) {
          observer.disconnect();

          stackEl.classList.add('is-revealing');

          window.requestAnimationFrame(function() {
            renderState();
            // Delay dot line calculation slightly so layouts can settle
            setTimeout(function() {
              updateDotsLine(dots, dotsContainer);
              
              // Remove revealing class after slower animation completes
              setTimeout(function() {
                stackEl.classList.remove('is-revealing');
              }, 1200); // Wait for the slow CSS transition to finish
            }, 100);
          });
        }
      }, { threshold: 0.3 });

      observer.observe(root);
    } else {
      // Fallback for older browsers
      window.requestAnimationFrame(function() {
        renderState();
        setTimeout(function() {
          updateDotsLine(dots, dotsContainer);
        }, 100);
      });
    }

    window.addEventListener('resize', function() {
      updateDotsLine(dots, dotsContainer);
    });
  }

  function initAll() {
    var containers = document.querySelectorAll('[data-ingredients-stack]');
    containers.forEach(initStack);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) {
      var roots = event.target.querySelectorAll('[data-ingredients-stack]');
      roots.forEach(initStack);
    }
  });

})();
